import { createSlice } from "@reduxjs/toolkit";
import {
  // Existing thunks
  createProductThunk,
  updateProductByIdThunk,
  updateProductStatusByIdThunk,
  deleteProductByIdThunk,
  getProductByIdThunk,
  getAllProductsThunk,

  // New pricing thunks
  getProductWithPricingThunk,

  // New stock management thunks
  updateLocationStockThunk,
  transferStockThunk,
  getLowStockProductsThunk,
  reserveStockThunk,
  checkAvailabilityThunk,

  // New branch/store thunks
  getProductsByBranchThunk,
  syncBranchInventoryThunk,

  // New bulk operations thunks
  bulkUpdateProductsThunk,
  importProductsThunk,
  exportProductsThunk,

  // New hallmark thunks
  updateHallmarkThunk,
  verifyHallmarkThunk,

  // Actions
  clearAdminProductError,
  clearProductPricing,
  clearStockTransferResult,
  clearBulkOperationResult,
  clearSelectedProduct,

  //price Rules
  getApplicablePricingRulesThunk,
  assignPricingRuleThunk,
  removePricingRuleThunk,
  getProductPricingRulesThunk,
} from "../thunks/adminProductThunks";

const initialState = {
  // Existing state
  products: [],
  product: null,
  loading: false,
  creating: false,
  updatingCards: [], // Track updating product IDs
  deletingCards: [], // NEW: Track deleting product IDs
  updating: false,
  error: null,
  pagination: null,

  // New state for pricing
  productPricing: null,
  pricingLoading: false,
  pricingError: null,

  // New state for stock management
  stockUpdating: false,
  stockTransferResult: null,
  lowStockProducts: [],
  stockReservation: null,
  productAvailability: null,
  stockError: null,

  // New state for branch/store
  branchProducts: [],
  branchProductsLoading: false,
  syncResults: null,

  // New state for bulk operations
  bulkOperationLoading: false,
  bulkOperationResult: null,
  importResult: null,
  exportLoading: false,

  // New state for hallmark
  hallmarkUpdating: false,
  hallmarkVerification: null,
  hallmarkError: null,

  pricingRules: {
    applicable: [], // Rules that can be applied to current product
    assigned: {}, // Currently assigned rules per branch { branchCode: ruleId }
    loading: false,
    assigning: false,
    error: null,
  },
};

const adminProductSlice = createSlice({
  name: "adminProduct",
  initialState,
  reducers: {
    clearPricingRules: (state) => {
      state.pricingRules = {
        applicable: [],
        assigned: {},
        loading: false,
        assigning: false,
        error: null,
      };
    },

    // Update assigned rules locally (optimistic update)
    updateAssignedRule: (state, action) => {
      const { branchCode, ruleId } = action.payload;
      if (ruleId) {
        state.pricingRules.assigned[branchCode] = ruleId;
      } else {
        delete state.pricingRules.assigned[branchCode];
      }
    },

    // Clear specific card states
    clearUpdatingCard: (state, action) => {
      state.updatingCards = state.updatingCards.filter(
        (id) => id !== action.payload
      );
    },
    clearDeletingCard: (state, action) => {
      state.deletingCards = state.deletingCards.filter(
        (id) => id !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== EXISTING REDUCERS =====
      .addCase(clearAdminProductError, (state) => {
        state.error = null;
        state.pricingError = null;
        state.stockError = null;
        state.hallmarkError = null;
      })
      .addCase(clearProductPricing, (state) => {
        state.productPricing = null;
        state.pricingError = null;
      })
      .addCase(clearStockTransferResult, (state) => {
        state.stockTransferResult = null;
      })
      .addCase(clearBulkOperationResult, (state) => {
        state.bulkOperationResult = null;
        state.importResult = null;
      })
      .addCase(clearSelectedProduct, (state) => {
        state.product = null;
        state.error = null;
      })

      // Create Product
      .addCase(createProductThunk.pending, (state) => {
        state.creating = true;
      })
      .addCase(createProductThunk.fulfilled, (state, action) => {
        state.creating = false;
        state.products.unshift(action.payload.data || action.payload);
      })
      .addCase(createProductThunk.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload;
      })

      // Update Product - Track individual card loading
      .addCase(updateProductByIdThunk.pending, (state, action) => {
        state.updatingCards.push(action.meta.arg.id);
        state.updating = true;
      })
      .addCase(updateProductByIdThunk.fulfilled, (state, action) => {
        state.updatingCards = state.updatingCards.filter(
          (id) => id !== action.meta.arg.id
        );
        const productData = action.payload.data || action.payload;
        const index = state.products.findIndex(
          (product) => product._id === productData._id
        );
        if (index !== -1) {
          state.products[index] = productData;
        }
        if (state.product?._id === productData._id) {
          state.product = productData;
        }
        state.updating = false;
      })
      .addCase(updateProductByIdThunk.rejected, (state, action) => {
        state.updatingCards = state.updatingCards.filter(
          (id) => id !== action.meta.arg.id
        );
        state.error = action.payload;
        state.updating = false;
      })

      // Update Product Status - Track individual card loading
      .addCase(updateProductStatusByIdThunk.pending, (state, action) => {
        state.updatingCards.push(action.meta.arg.id);
        state.updating = true;
      })
      .addCase(updateProductStatusByIdThunk.fulfilled, (state, action) => {
        state.updatingCards = state.updatingCards.filter(
          (id) => id !== action.meta.arg.id
        );
        const productData = action.payload.data || action.payload;
        const index = state.products.findIndex(
          (product) => product._id === productData._id
        );
        if (index !== -1) {
          state.products[index].status = productData.status;
        }
        if (state.product?._id === productData._id) {
          state.product.status = productData.status;
        }
        state.updating = false;
      })
      .addCase(updateProductStatusByIdThunk.rejected, (state, action) => {
        state.updatingCards = state.updatingCards.filter(
          (id) => id !== action.meta.arg.id
        );
        state.error = action.payload;
        state.updating = false;
      })

      // Delete Product - Track individual card, NOT main loader
      .addCase(deleteProductByIdThunk.pending, (state, action) => {
        // Add to deletingCards instead of setting loading = true
        state.deletingCards.push(action.meta.arg);
      })
      .addCase(deleteProductByIdThunk.fulfilled, (state, action) => {
        const deletedId = action.payload._id || action.meta.arg;
        // Remove from deletingCards
        state.deletingCards = state.deletingCards.filter(
          (id) => id !== deletedId
        );
        // Remove product from list
        state.products = state.products.filter(
          (product) => product._id !== deletedId
        );
      })
      .addCase(deleteProductByIdThunk.rejected, (state, action) => {
        // Remove from deletingCards
        state.deletingCards = state.deletingCards.filter(
          (id) => id !== action.meta.arg
        );
        state.error = action.payload;
      })

      // Get Product by ID
      // Clear pricing rules when product changes
      .addCase(getProductByIdThunk.pending, (state) => {
        state.loading = true;
        // Clear pricing rules when loading a new product
        state.pricingRules = initialState.pricingRules;
      })
      .addCase(getProductByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.product = action.payload;
      })
      .addCase(getProductByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get All Products
      .addCase(getAllProductsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllProductsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.pagination = action.payload.pagination;
        state.products = action.payload.products;
      })
      .addCase(getAllProductsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Low Stock Products
      .addCase(getLowStockProductsThunk.pending, (state) => {
        // Don't set loading = true to avoid replacing main content
        state.stockError = null;
      })
      .addCase(getLowStockProductsThunk.fulfilled, (state, action) => {
        state.lowStockProducts = action.payload.data || action.payload;
      })
      .addCase(getLowStockProductsThunk.rejected, (state, action) => {
        state.stockError = action.payload;
      })

      // Get applicable pricing rules
      .addCase(getApplicablePricingRulesThunk.pending, (state) => {
        state.pricingRules.loading = true;
        state.pricingRules.error = null;
      })
      .addCase(getApplicablePricingRulesThunk.fulfilled, (state, action) => {
        state.pricingRules.loading = false;
        state.pricingRules.applicable = action.payload.data || [];
      })
      .addCase(getApplicablePricingRulesThunk.rejected, (state, action) => {
        state.pricingRules.loading = false;
        state.pricingRules.error = action.payload;
      })

      // Get product's assigned pricing rules
      .addCase(getProductPricingRulesThunk.pending, (state) => {
        state.pricingRules.loading = true;
      })
      .addCase(getProductPricingRulesThunk.fulfilled, (state, action) => {
        state.pricingRules.loading = false;
        // Convert array of branch rules to object format
        const assigned = {};
        (action.payload.data || []).forEach((rule) => {
          assigned[rule.branchCode] = rule.pricingRuleId;
        });
        state.pricingRules.assigned = assigned;
      })
      .addCase(getProductPricingRulesThunk.rejected, (state, action) => {
        state.pricingRules.loading = false;
        state.pricingRules.error = action.payload;
      })

      // Assign pricing rule
      .addCase(assignPricingRuleThunk.pending, (state) => {
        state.pricingRules.assigning = true;
        state.pricingRules.error = null;
      })
      .addCase(assignPricingRuleThunk.fulfilled, (state, action) => {
        state.pricingRules.assigning = false;
        const { branchCode, ruleId } = action.payload.data;
        state.pricingRules.assigned[branchCode] = ruleId;

        // Also update the product's pricingConfig if it's loaded
        if (state.product) {
          if (!state.product.pricingConfig) {
            state.product.pricingConfig = { branchSpecificRules: [] };
          }

          // Remove existing rule for this branch
          state.product.pricingConfig.branchSpecificRules =
            state.product.pricingConfig.branchSpecificRules.filter(
              (r) => r.branchCode !== branchCode
            );

          // Add new rule
          state.product.pricingConfig.branchSpecificRules.push({
            branchCode,
            pricingRuleId: ruleId,
            appliedFrom: new Date().toISOString(),
          });
        }
      })
      .addCase(assignPricingRuleThunk.rejected, (state, action) => {
        state.pricingRules.assigning = false;
        state.pricingRules.error = action.payload;
      })

      // Remove pricing rule
      .addCase(removePricingRuleThunk.pending, (state) => {
        state.pricingRules.assigning = true;
      })
      .addCase(removePricingRuleThunk.fulfilled, (state, action) => {
        state.pricingRules.assigning = false;
        const { branchCode } = action.payload.data;
        delete state.pricingRules.assigned[branchCode];

        // Also update the product's pricingConfig if it's loaded
        if (state.product?.pricingConfig?.branchSpecificRules) {
          state.product.pricingConfig.branchSpecificRules =
            state.product.pricingConfig.branchSpecificRules.filter(
              (r) => r.branchCode !== branchCode
            );
        }
      })
      .addCase(removePricingRuleThunk.rejected, (state, action) => {
        state.pricingRules.assigning = false;
        state.pricingRules.error = action.payload;
      })

      // Bulk Update Products
      .addCase(bulkUpdateProductsThunk.pending, (state) => {
        state.bulkOperationLoading = true;
      })
      .addCase(bulkUpdateProductsThunk.fulfilled, (state, action) => {
        state.bulkOperationLoading = false;
        state.bulkOperationResult = action.payload;
      })
      .addCase(bulkUpdateProductsThunk.rejected, (state, action) => {
        state.bulkOperationLoading = false;
        state.error = action.payload;
      })

      // Import Products
      .addCase(importProductsThunk.pending, (state) => {
        state.bulkOperationLoading = true;
      })
      .addCase(importProductsThunk.fulfilled, (state, action) => {
        state.bulkOperationLoading = false;
        state.importResult = action.payload;
      })
      .addCase(importProductsThunk.rejected, (state, action) => {
        state.bulkOperationLoading = false;
        state.error = action.payload;
      })

      // Export Products
      .addCase(exportProductsThunk.pending, (state) => {
        state.exportLoading = true;
      })
      .addCase(exportProductsThunk.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(exportProductsThunk.rejected, (state, action) => {
        state.exportLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearPricingRules,
  updateAssignedRule,
  clearUpdatingCard,
  clearDeletingCard,
} = adminProductSlice.actions;
export default adminProductSlice.reducer;
